/* useLottieOnce — one-shot success microinteraction trigger.

   Returns a tuple: [trigger, surface]
   - `trigger()` mounts a new Lottie instance that plays once and self-unmounts
     on complete. Multiple triggers concurrently → each renders independently.
   - `surface` is the JSX element to embed where you want the animations to
     appear (typically anchored over a row, button, or icon).

   Respects `prefers-reduced-motion` synchronously: trigger() becomes a no-op
   and `surface` returns null, so the call site stays unchanged.

   Usage:
     const [fireCheck, checkSurface] = useLottieOnce(goldCheckLottie, { size: 80 });
     return <button onClick={async () => { await api.publish(); fireCheck(); }}>
       Publish {checkSurface}
     </button>;
*/
import { useCallback, useRef, useState } from "react";
import LottieEditorial from "@/components/editorial/LottieEditorial";

function prefersReducedMotion() {
  return (
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

export default function useLottieOnce(animation, { size = 80, className } = {}) {
  const [instances, setInstances] = useState([]);
  const nextId = useRef(0);

  const trigger = useCallback(() => {
    if (prefersReducedMotion()) return;
    const id = nextId.current++;
    setInstances((arr) => [...arr, id]);
  }, []);

  const cleanup = useCallback((id) => {
    setInstances((arr) => arr.filter((x) => x !== id));
  }, []);

  const surface = (
    <span
      aria-hidden="true"
      style={{ position: "absolute", inset: 0, pointerEvents: "none", display: "inline-flex", alignItems: "center", justifyContent: "center" }}
    >
      {instances.map((id) => (
        <LottieEditorial
          key={id}
          animation={animation}
          size={size}
          loop={false}
          autoplay
          onComplete={() => cleanup(id)}
          className={className}
          data-testid={`lottie-once-${id}`}
        />
      ))}
    </span>
  );

  return [trigger, surface];
}
